import { PENDING_UPLOAD } from '@/components/Collection/utils';
import {
  CategoryBannerDestinationType,
  CategoryBannerShippingBehavior,
} from '@/graphql/types/generated';
import { slugify } from '@/utils/str';

export type BannerImageKey =
  | 'desktopImage'
  | 'mobileImage'
  | 'shippingDesktopImage'
  | 'shippingMobileImage';

/** Every creative in the form carries the same four images. */
type ImageValues = Record<BannerImageKey, string>;

/**
 * The creative every market falls back to for whatever it does not set itself.
 * A market's is the same shape plus the market, so both are stated once here.
 */
export type DefaultCreative = ImageValues & {
  shippingBehavior: CategoryBannerShippingBehavior;
  /**
   * Where the banner links to. The form edits a Collection destination and
   * sends both fields back on every save, since an omitted field is not left
   * alone, it is reset to the schema's default.
   */
  destinationType: CategoryBannerDestinationType | null;
  destinationRef: string | null;
};

/** One creative's link, as the picker reads and writes it. */
export type BannerDestination = Pick<
  DefaultCreative,
  'destinationType' | 'destinationRef'
>;

export type MarketCreative = DefaultCreative & {
  warehouseId: string;
  /**
   * True only when this market is explicitly hidden. Distinct from every other
   * empty market, which inherits the shared creative rather than showing nothing.
   */
  hidden: boolean;
};

/** Shipping creative is sent only where it is read, hence the optionality. */
export type DefaultInput = Omit<
  DefaultCreative,
  'shippingDesktopImage' | 'shippingMobileImage'
> &
  Partial<Pick<ImageValues, 'shippingDesktopImage' | 'shippingMobileImage'>>;

/**
 * A hidden market's creative is optional and its shipping fields never sent,
 * so this is `DefaultInput` loosened to all-optional rather than a stricter
 * union — the schema itself only ever requires `warehouseId`.
 */
export type MarketInput = Partial<DefaultInput> & {
  warehouseId: string;
  hidden?: boolean;
};

type Market = { id: string };

type StoredCreative = Partial<{
  [Key in keyof DefaultCreative]: DefaultCreative[Key] | null;
}>;

type ConfiguredMarket = StoredCreative & {
  warehouse: { id: string };
  hidden: boolean;
};

/**
 * Which creative a field belongs to — the shared default, or one market.
 *
 * The default is not a member of `warehouses`, so it has no index to be named
 * by. Naming the two cases keeps one generator for the whole key space instead
 * of leaving each call site to decide when an index applies.
 */
export type CreativeTarget =
  | { kind: 'default' }
  | { kind: 'market'; index: number };

export const defaultTarget: CreativeTarget = { kind: 'default' };

export const marketTarget = (index: number): CreativeTarget => ({
  kind: 'market',
  index,
});

/**
 * Where one creative's image lives in the form.
 *
 * Defined once because three things have to agree on it: the field the modal
 * renders, the key the pending-files map uses, and the lookup that swaps an
 * uploaded key in on submit. The default takes the flat name, as
 * `CreateCollectionPromoPage` does against this same mechanism.
 */
export const creativeFieldName = (
  target: CreativeTarget,
  key: BannerImageKey,
) => (target.kind === 'default' ? key : `warehouses[${target.index}].${key}`);

/**
 * The market spelling of the same name, which the modal and the market table
 * address their fields by. A market's index is its position in the full market
 * list, which never changes while the form is open.
 */
export const imageFieldName = (index: number, key: BannerImageKey) =>
  creativeFieldName(marketTarget(index), key);

/**
 * A creative is part of the banner only once it has both images. This reads
 * the form as it stands, so a picked-but-unuploaded file counts; whether a
 * creative is worth saving is `toCreativeInput`'s answer, not this one.
 */
export const hasArtwork = (
  creative: Pick<ImageValues, 'desktopImage' | 'mobileImage'> | undefined,
) => Boolean(creative?.desktopImage && creative?.mobileImage);

/**
 * True only when exactly one of the two images is set. A creative in this
 * state has nothing worth saving — `toCreativeInput` treats it the same as
 * having neither, which for the default means deleting whatever was stored.
 * The form is what has to stop a save from reaching this state.
 */
export const hasPartialArtwork = (
  creative: Pick<ImageValues, 'desktopImage' | 'mobileImage'>,
) => Boolean(creative.desktopImage) !== Boolean(creative.mobileImage);

/**
 * One creative's images as they should be saved: a freshly uploaded key where
 * the picker left one, the stored key otherwise. The placeholder reads as
 * empty — it is a marker that a file is pending, never a key worth storing.
 */
const resolveImages =
  (
    target: CreativeTarget,
    creative: ImageValues,
    uploaded: Record<string, string>,
  ) =>
  (key: BannerImageKey) => {
    const stored = creative[key];
    return (
      uploaded[creativeFieldName(target, key)] ??
      (stored === PENDING_UPLOAD ? '' : stored)
    );
  };

/**
 * One creative as the mutation takes it, or null when it has no artwork — a
 * market with none inherits, and a default with none is being removed.
 */
const toCreativeInput = (
  target: CreativeTarget,
  creative: DefaultCreative,
  uploaded: Record<string, string>,
): DefaultInput | null => {
  const resolved = resolveImages(target, creative, uploaded);
  const desktopImage = resolved('desktopImage');
  const mobileImage = resolved('mobileImage');
  if (!hasArtwork({ desktopImage, mobileImage })) return null;
  return {
    desktopImage,
    mobileImage,
    shippingBehavior: creative.shippingBehavior,
    destinationType: creative.destinationType,
    destinationRef: creative.destinationRef,
    // Sent only where they are read. The catalog ignores them otherwise, so
    // including them would store creative that has stopped being shown.
    ...(creative.shippingBehavior === CategoryBannerShippingBehavior.Override
      ? {
          shippingDesktopImage: resolved('shippingDesktopImage'),
          shippingMobileImage: resolved('shippingMobileImage'),
        }
      : {}),
  };
};

/**
 * The form entry for one stored creative, market or shared, whether or not
 * anything is stored for it today. Both go through here so a blank market and
 * a blank default cannot start out meaning different things.
 */
export const toDefaultCreative = (
  stored: StoredCreative | null | undefined,
): DefaultCreative => ({
  desktopImage: stored?.desktopImage ?? '',
  mobileImage: stored?.mobileImage ?? '',
  shippingBehavior:
    stored?.shippingBehavior ?? CategoryBannerShippingBehavior.Inherit,
  shippingDesktopImage: stored?.shippingDesktopImage ?? '',
  shippingMobileImage: stored?.shippingMobileImage ?? '',
  destinationType: stored?.destinationType ?? null,
  destinationRef: stored?.destinationRef ?? null,
});

/**
 * One form entry per market, whether or not it has creative today.
 *
 * Markets are never added to or removed from this list while the form is open,
 * so a market's index is stable and the field names built from it keep pointing
 * at the same market. Clearing a market blanks its images rather than dropping
 * the entry; `toMarketInputs` is what leaves it out of the save.
 */
export const toMarketCreatives = (
  markets: Market[],
  configured: ConfiguredMarket[],
): MarketCreative[] => {
  const byWarehouse = new Map(
    configured.map((entry) => [entry.warehouse.id, entry]),
  );
  return markets.map((market) => ({
    warehouseId: market.id,
    hidden: byWarehouse.get(market.id)?.hidden ?? false,
    ...toDefaultCreative(byWarehouse.get(market.id)),
  }));
};

/**
 * The markets to save, with freshly uploaded images swapped in for the
 * placeholder the picker left behind.
 *
 * A market with no creative of its own is left out rather than sent empty,
 * which deletes whatever it had and drops it back to the shared creative at
 * render time. The mutation replaces the whole `warehouses` set, so a caller
 * must send back every market it means to keep.
 */
export const toMarketInputs = (
  markets: MarketCreative[],
  uploaded: Record<string, string>,
): MarketInput[] =>
  markets.flatMap((market, index): MarketInput[] => {
    const target = marketTarget(index);
    // A hide is a positive signal the save must not drop, so it is included even
    // when the market carries no creative of its own — the ordinary case, since
    // the usual market to hide is one that was inheriting the shared creative.
    // Its shipping behaviour and destination still round-trip while hidden
    // (an omitted field is not left alone, it is reset), so un-hiding resumes
    // exactly what was configured before rather than a schema default.
    if (market.hidden) {
      const resolved = resolveImages(target, market, uploaded);
      const desktopImage = resolved('desktopImage');
      const mobileImage = resolved('mobileImage');
      return [
        {
          warehouseId: market.warehouseId,
          hidden: true,
          shippingBehavior: market.shippingBehavior,
          destinationType: market.destinationType,
          destinationRef: market.destinationRef,
          ...(hasArtwork({ desktopImage, mobileImage })
            ? { desktopImage, mobileImage }
            : {}),
          ...(market.shippingBehavior ===
          CategoryBannerShippingBehavior.Override
            ? {
                shippingDesktopImage: resolved('shippingDesktopImage'),
                shippingMobileImage: resolved('shippingMobileImage'),
              }
            : {}),
        },
      ];
    }
    const input = toCreativeInput(target, market, uploaded);
    return input ? [{ warehouseId: market.warehouseId, ...input }] : [];
  });

/**
 * The shared creative to save, or null to remove the stored one — including
 * when only one image is filled in, since the mutation requires both.
 *
 * Null rather than an omission because the mutation reads the two differently:
 * leaving `defaultCreative` out keeps whatever is stored, since a call naming
 * only per-market creative says nothing about the default. So a half-filled
 * form removes the default rather than failing, and the form is what has to
 * stop the user reaching that state.
 */
export const toDefaultInput = (
  creative: DefaultCreative,
  uploaded: Record<string, string>,
): DefaultInput | null => toCreativeInput(defaultTarget, creative, uploaded);

/**
 * A creative overrides for shipping but hasn't been given the artwork that
 * requires — the shared half of the check `marketsMissingShippingCreative`
 * and `defaultMissingShippingCreative` both apply, so a market and the
 * default can't drift into checking it differently.
 */
const missingShippingCreative = (
  creative: Pick<
    DefaultCreative,
    'shippingBehavior' | 'shippingDesktopImage' | 'shippingMobileImage'
  >,
) =>
  creative.shippingBehavior === CategoryBannerShippingBehavior.Override &&
  !(creative.shippingDesktopImage && creative.shippingMobileImage);

/**
 * Markets that override for shipping but have not been given the creative to
 * show. Saving one is rejected by the catalog naming that market, so the form
 * holds it back and says which market needs artwork.
 */
export const marketsMissingShippingCreative = (
  markets: MarketCreative[],
): MarketCreative[] =>
  markets.filter(
    (market) =>
      !market.hidden && hasArtwork(market) && missingShippingCreative(market),
  );

/**
 * Whether the default itself overrides for shipping without the artwork —
 * the same gap `marketsMissingShippingCreative` catches per market, but the
 * default is edited inline on the page rather than through a market row, so
 * it needed its own entry point into the same check.
 */
export const defaultMissingShippingCreative = (
  creative: Pick<
    DefaultCreative,
    | 'desktopImage'
    | 'mobileImage'
    | 'shippingBehavior'
    | 'shippingDesktopImage'
    | 'shippingMobileImage'
  >,
) => hasArtwork(creative) && missingShippingCreative(creative);

/** The link choices the form offers for one creative. */
export type DestinationChoice = 'none' | 'collection';

/** Reads the stored destination as the link choice the form shows. */
export const destinationChoice = (
  creative: Pick<DefaultCreative, 'destinationType'>,
): DestinationChoice =>
  creative.destinationType === CategoryBannerDestinationType.Collection ||
  creative.destinationType === CategoryBannerDestinationType.InternalPath
    ? 'collection'
    : 'none';

/** Starts a link choice with no collection picked, or clears both link fields. */
export const destinationFor = (choice: DestinationChoice): BannerDestination =>
  choice === 'collection'
    ? {
        destinationType: CategoryBannerDestinationType.Collection,
        destinationRef: '',
      }
    : { destinationType: null, destinationRef: null };

/** A collection link's slug and campaign text, as the form edits them. */
export type ParsedCampaignDestination = {
  slug: string;
  campaignText: string;
  unsupported: boolean;
};

const internalPathPattern = /^\/catalog\/collection\/([^/?]+)\?(.*)$/;

/**
 * Reads a stored destination into the slug and campaign text the form edits.
 * An `InternalPath` outside that shape is marked `unsupported`.
 */
export const parseCampaignDestination = (
  creative: Pick<DefaultCreative, 'destinationType' | 'destinationRef'>,
): ParsedCampaignDestination => {
  if (creative.destinationType === CategoryBannerDestinationType.Collection) {
    return {
      slug: creative.destinationRef ?? '',
      campaignText: '',
      unsupported: false,
    };
  }
  if (creative.destinationType === CategoryBannerDestinationType.InternalPath) {
    const match = (creative.destinationRef ?? '').match(internalPathPattern);
    if (!match) return { slug: '', campaignText: '', unsupported: true };
    const [, slug, query] = match;
    const campaignText = new URLSearchParams(query).get('utm_campaign') ?? '';
    return { slug, campaignText, unsupported: false };
  }
  return { slug: '', campaignText: '', unsupported: false };
};

/**
 * Builds the destination to save; campaign text adds UTM params via InternalPath.
 *
 * A blank slug, or campaign text that slugifies to nothing (empty or made entirely
 * of characters `slugify` strips), falls back to a plain `Collection` destination —
 * matching #630's save shape exactly, including the "missing collection" validation
 * a cleared slug still needs to trigger. There is nowhere to hold campaign text that
 * has no slug to attach to, so it is dropped rather than carried as orphaned state.
 */
export const buildCampaignDestination = (
  slug: string,
  campaignText: string,
): BannerDestination => {
  const campaignSlug = slugify(campaignText);
  if (!slug || !campaignSlug) {
    return {
      destinationType: CategoryBannerDestinationType.Collection,
      destinationRef: slug,
    };
  }
  const qs = new URLSearchParams({
    utm_source: 'odeko_portal',
    utm_medium: 'category_banner',
    utm_campaign: campaignSlug,
  }).toString();
  return {
    destinationType: CategoryBannerDestinationType.InternalPath,
    destinationRef: `/catalog/collection/${slug}?${qs}`,
  };
};

/**
 * A Collection link without a collection picked. Markets and the shared
 * default use the same check so their destination requirements stay in step.
 */
const missingDestination = (creative: BannerDestination) =>
  creative.destinationType === CategoryBannerDestinationType.Collection &&
  !creative.destinationRef;

/**
 * Visible markets with artwork that link to a Collection but have none picked.
 * Empty markets inherit, so their destination choice does not need a check.
 */
export const marketsMissingDestination = (
  markets: MarketCreative[],
): MarketCreative[] =>
  markets.filter(
    (market) =>
      !market.hidden && hasArtwork(market) && missingDestination(market),
  );

/**
 * Whether the shared creative has artwork and a Collection link with no pick.
 * This is the same gap checked per market by `marketsMissingDestination`.
 */
export const defaultMissingDestination = (
  creative: Pick<
    DefaultCreative,
    'desktopImage' | 'mobileImage' | 'destinationType' | 'destinationRef'
  >,
) => hasArtwork(creative) && missingDestination(creative);

/**
 * What a market row shows, in the four states the editor has to tell apart.
 * `hasDefault` is whether the shared creative itself has artwork — a market
 * inheriting a default with none is the same as never having had a banner.
 */
export type MarketStatus = 'own' | 'hidden' | 'inherited' | 'none';

export const marketStatus = (
  market: Pick<MarketCreative, 'hidden' | 'desktopImage' | 'mobileImage'>,
  hasDefault: boolean,
): MarketStatus => {
  if (market.hidden) return 'hidden';
  if (hasArtwork(market)) return 'own';
  return hasDefault ? 'inherited' : 'none';
};

export type HiddenMarket = {
  /** The market's position in the form's warehouses list. */
  index: number;
  name: string;
  stocked: boolean;
  hidden: boolean;
  /** Hidden when the form loaded, so un-hiding it this session stays undoable. */
  wasHidden: boolean;
};

type HiddenMarketEntry = {
  index: number;
  name: string;
  checked: boolean;
  disabled: boolean;
  label: string;
};

const byName = (a: HiddenMarket, b: HiddenMarket) =>
  a.name.localeCompare(b.name);

/**
 * One checkbox entry per market, stocked first and then alphabetical. Disabling
 * only ever blocks a new hide on an unstocked market: one hidden when the form
 * loaded stays toggleable both ways, even once unchecked.
 */
export const hiddenMarketEntries = (
  markets: HiddenMarket[],
): HiddenMarketEntry[] => {
  const ordered = [
    ...markets.filter((market) => market.stocked).sort(byName),
    ...markets.filter((market) => !market.stocked).sort(byName),
  ];
  return ordered.map((market) => {
    const disabled = !market.hidden && !market.wasHidden && !market.stocked;
    return {
      index: market.index,
      name: market.name,
      checked: market.hidden,
      disabled,
      label: disabled ? `${market.name} (not stocked)` : market.name,
    };
  });
};

/** Indexes of every hidden market, so "Unhide all" never touches the rest. */
export const unhideAllIndexes = (markets: HiddenMarket[]): number[] =>
  markets.filter((market) => market.hidden).map((market) => market.index);
